import Layout from "../layout/Layout";
import {Header} from "@amzn/awsui-components-react";
import React, {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import Button from "@amzn/awsui-components-react/polaris/button";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar"
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import {
    MAP_PACK_RELEASE_STATE_ID_TABLE_COLUMN_DEFINITIONS,
    MAP_PACK_RELEASE_SUMMARY_TABLE_COLUMN_DEFINITIONS,
    MAP_PACK_RELEASE_SUMMARY_TABLE_FILTER_PROPERTIES,
    MAP_PACK_RELEASE_SUMMARY_TABLE_VISIBLE_CONTENT_OPTIONS,
    PAGE_SIZE_OPTIONS
} from "../../utils/mapReleaseDashboard/mapPackReleaseSummaryTableConfig";
import ComplexFilterTable from "../offline-maps/ComplexFilterTable";
import {useMapReleaseStore} from "../../store/MapReleaseStore";
import {ButtonDropdown, ButtonDropdownProps} from "@amzn/awsui-components-react";
import { addApprovalStatus } from "../../utils/mapReleaseDashboard/mapReleaseDataUtils";

// Type definitions
interface ValidationNestedItem {
    mapPackVersion: string;
    summaryId: string;
    validationSummary: string;
    validationStatus: string;
    approvers: string;
    approvalStatus?: string;
}

interface ValidationDataItem {
    rsmVersion: string;
    rsmControlVersion: string;
    trecsRecostingLink: string;
    stateID: string;
    approved: number | string;
    totalApprovers: number | string;
    MCM: string;
    release: string;
    releaseStatus?: string;
    totalPendingApprovals?: number;
    nestedItems: ValidationNestedItem[];
}

enum Region {
  NA = "NA",
  FE = "FE",
  EU = "EU",
  IN = "IN"
}

const MapReleaseManagement = () => {
    const activeValidationDataFetchStatus = false;
    const fetchMapPackReleaseSummary = useMapReleaseStore(
        (state) => state.fetchMapPackReleaseSummary
    );
    const fetchApprovalStatus = useMapReleaseStore(
        (state) => state.fetchConsensusApprovalStatus
    );
    const fetchReleaseStatus = useMapReleaseStore(
        (state) => state.fetchReleaseStatus
    );
    const activeValidationData = useMapReleaseStore(
        (state) => state.getMapPackReleaseSummary()
    );
    const activeApprovalStatus = useMapReleaseStore(
        (state) => state.getConsensusApprovalStatus()
    );
    const activeReleaseStatus = useMapReleaseStore(
        (state) => state.getReleaseStatus()
    );
    const isApprovalStatusLoaded = useMapReleaseStore(
        (state) => state.isConsensusApprovalStatusLoaded()
    );
    const isReleaseStatusLoaded = useMapReleaseStore(
        (state) => state.isReleaseStatusLoaded()
    );
    const [completeValidationData, setCompleteValidationData] = React.useState<ValidationDataItem[]>([]);

    const mapPackValidationSummaryTablePreferences = useMapReleaseStore(
        (state) => state.mapPackReleaseSummaryTablePreferences
    );
    const setMapPackReleaseSummaryTablePreferences = useMapReleaseStore(
        (state) => state.setMapPackReleaseSummaryTablePreferences
    );
    const isMapPackReleaseLoaded = useMapReleaseStore(
        (state) => state.isMapPackReleaseLoaded()
    );
    const invalidMapPackReleaseSummary = useMapReleaseStore(
        (state) => state.getInvalidMapPackReleaseSummary()
    );
    const setCurrentRegion = useMapReleaseStore(
        (state) => state.setCurrentRegion
    );
    const currentRegion = useMapReleaseStore(
        (state) => state.currentRegion
    );
    const [buttonState, setButtonState] = React.useState(false);
    const [lastRefreshTime, setLastRefreshTime] = React.useState<Date | null>(null);

    const handleChange = () => {
        fetchApprovalStatus(currentRegion);
        fetchReleaseStatus(currentRegion);
        setButtonState(true);
        setLastRefreshTime(new Date());
    }
    
    React.useEffect(() => {
        fetchMapPackReleaseSummary(Region.NA);
        fetchApprovalStatus(Region.NA);
        fetchReleaseStatus(Region.NA);
    }, []); // Only run on mount
    
    React.useEffect(() => {
        // Set up periodic refresh of approval data every 30 seconds
        const refreshInterval = setInterval(() => {
            console.log("Auto-refreshing approval data...");
            fetchApprovalStatus(currentRegion);
            setLastRefreshTime(new Date());
        }, 30000); // 30 seconds
        
        // Clean up interval on component unmount
        return () => clearInterval(refreshInterval);
    }, [currentRegion]); // Re-create interval when region changes

    React.useEffect(() => {
        if (isApprovalStatusLoaded && isReleaseStatusLoaded && buttonState) {
            setButtonState(false);
        }
    }, [isApprovalStatusLoaded, isReleaseStatusLoaded]);

    React.useEffect(() => {
        const updatedData = addApprovalStatus(
            activeValidationData || [], 
            activeApprovalStatus || [], 
            activeReleaseStatus || []
        );
        setCompleteValidationData(updatedData);
    }, [activeValidationData, activeApprovalStatus, activeReleaseStatus]);

    const CustomFlashbar: React.FC = () => {
            return <Flashbar items={[
                {
                    header: "Error",
                    type: undefined,
                    content: "Could not load map pack release summary ",
                    dismissible: false,
                    dismissLabel: "Dismiss message",
                    id: "message_1"
                }
            ]}/>;
    }
    
    return (
        <Layout>
            <Header variant="h1">MapPack Release Management Portal</Header>
            <div>
                <ButtonDropdown
                    items={[{
                        text: "Options",
                        items: [
                            {text: "NA", id: Region.NA},
                            {text: "FE", id: Region.FE},
                            {text: "EU", id: Region.EU},
                            {text: "IN", id: Region.IN}
                        ]
                    }]}
                    ariaLabel="Map Actions"
                    onItemClick={(evt: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
                        fetchMapPackReleaseSummary(evt.detail.id);
                        setCurrentRegion(evt.detail.id);
                        fetchApprovalStatus(evt.detail.id);
                        fetchReleaseStatus(evt.detail.id);
                    }}
                    variant="primary"
                >Region</ButtonDropdown>
            </div>
            <br/>
            {invalidMapPackReleaseSummary && isMapPackReleaseLoaded ? ([<CustomFlashbar/>, <br/>])
                :(!isMapPackReleaseLoaded ? ([
                                    <Spinner size="large"/>,
                                    <br/>, <br/>]
                            ) : (
                [<div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <Button
                        variant="primary"
                        loading={buttonState}
                        iconName="refresh"
                        onClick={() => {
                            handleChange();
                    }} > 
                    Refresh Approval Status
                    </Button>
                    {lastRefreshTime && (
                        <span style={{ marginLeft: '10px', color: '#5f6b7a', fontSize: '14px' }}>
                            Last refreshed: {lastRefreshTime.toLocaleTimeString()}
                        </span>
                    )}
                </div>, 
                <ComplexFilterTable
                    itemList={completeValidationData}
                    columnDefinitions={MAP_PACK_RELEASE_SUMMARY_TABLE_COLUMN_DEFINITIONS}
                    visibleContentOptions={MAP_PACK_RELEASE_SUMMARY_TABLE_VISIBLE_CONTENT_OPTIONS}
                    fetchingData={activeValidationDataFetchStatus}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    filterProperties={MAP_PACK_RELEASE_SUMMARY_TABLE_FILTER_PROPERTIES}
                    preferences={mapPackValidationSummaryTablePreferences}
                    setPreferences={setMapPackReleaseSummaryTablePreferences}
                    tableName={"Map Release Management Dashboard - " + currentRegion}
                    nestedTableColumnDefinitions={MAP_PACK_RELEASE_STATE_ID_TABLE_COLUMN_DEFINITIONS}
                />]))}
        </Layout>
    );
};

export default MapReleaseManagement;
